import {
  Autocomplete,
  Chip,
  Link,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { validate as validateEmail } from 'email-validator';
import { FocusEvent, ReactNode, useEffect, useState } from 'react';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { useIntl } from 'react-intl';

type Field = Partial<
  ControllerRenderProps<
    { to: string[]; cc: string[]; bcc: string[] } & unknown,
    'to' | 'cc' | 'bcc'
  >
>;

const EmailTypeahead = ({
  label,
  endAdornment,
  options,
  field,
  fieldState,
  validate = validateEmail,
}: {
  label: string;
  endAdornment?: ReactNode;
  options: string[] | undefined;
  field: Field;
  fieldState?: Partial<ControllerFieldState>;
  validate?: (email: string) => boolean;
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!value) return;

    field.onChange?.([...(field.value ?? []), value]);
    field.onBlur?.();
  };

  return (
    <Autocomplete
      {...field}
      onChange={(_e, v) => {
        if (v) {
          field.onChange?.(v ?? '');
        }
      }}
      options={options ?? []}
      clearOnBlur
      onBlur={handleOnBlur}
      renderInput={params => (
        <TextField
          {...params}
          autoFocus
          label={label}
          size="small"
          error={Boolean(fieldState?.error)}
          helperText={fieldState?.error ? fieldState.error.message : ''}
          InputProps={{
            ...params.InputProps,
            style: endAdornment ? { paddingRight: 120 } : {},
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {endAdornment}
              </>
            ),
          }}
        />
      )}
      disablePortal
      freeSolo
      multiple
      open={!!inputValue.length}
      renderOption={(props, option) => (
        <li {...props}>
          <Typography fontSize="small">{option}</Typography>
        </li>
      )}
      renderTags={(_value, getTagProps) => {
        return _value.map((chip, i) => (
          <Chip
            {...getTagProps({ index: i })}
            color={validate(chip) ? 'default' : 'error'}
            label={chip}
            key={`chip-${chip}`}
          />
        ));
      }}
      inputValue={inputValue}
      onInputChange={(_, newValue) => setInputValue(newValue)}
    />
  );
};

// Will this be re-usable in Email Response form?
export const EmailRecipients = ({
  suggestedRecipientEmails,
  to,
  cc,
  bcc,
}: {
  suggestedRecipientEmails: string[] | undefined;
  to: { field: Field; fieldState?: Partial<ControllerFieldState> };
  cc: { field: Field; fieldState?: Partial<ControllerFieldState> };
  bcc: { field: Field; fieldState?: Partial<ControllerFieldState> };
}) => {
  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);

  useEffect(() => {
    if (cc.field.value && cc.field.value.length > 0) {
      setCcVisible(true);
    }
  }, [cc.field.value]);

  useEffect(() => {
    if (bcc.field.value && bcc.field.value.length > 0) {
      setBccVisible(true);
    }
  }, [bcc.field.value]);

  const intl = useIntl();
  return (
    <>
      <EmailTypeahead
        {...to}
        options={suggestedRecipientEmails}
        label={intl.formatMessage({
          id: 'components.emailResponseForm.fields.edit.to.label',
        })}
        endAdornment={
          <Stack
            direction="row"
            sx={{
              marginRight: '32px',
              position: 'absolute',
              right: 0,
            }}
          >
            <Link
              variant="body2"
              color={ccVisible ? 'primary' : 'inherit'}
              sx={{
                mx: 1,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (ccVisible === true) {
                  cc.field.onChange?.([]);
                }
                setCcVisible(!ccVisible);
              }}
            >
              {intl.formatMessage({
                id: 'components.emailResponseForm.fields.edit.buttons.copy.label',
              })}
            </Link>
            <Link
              variant="body2"
              color={bccVisible ? 'primary' : 'inherit'}
              sx={{
                mx: 1,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (bccVisible === true) {
                  bcc.field.onChange?.([]);
                }
                setBccVisible(!bccVisible);
              }}
            >
              {intl.formatMessage({
                id: 'components.emailResponseForm.fields.edit.buttons.blindCopy.label',
              })}
            </Link>
          </Stack>
        }
      />
      {ccVisible && (
        <EmailTypeahead
          {...cc}
          options={suggestedRecipientEmails}
          label={intl.formatMessage({
            id: 'components.emailResponseForm.fields.edit.cc.label',
          })}
        />
      )}
      {bccVisible && (
        <EmailTypeahead
          {...bcc}
          options={suggestedRecipientEmails}
          label={intl.formatMessage({
            id: 'components.emailResponseForm.fields.edit.bcc.label',
          })}
        />
      )}
    </>
  );
};
